import React, {
    memo,
    useContext,
} from 'react';
import { useSelector } from 'react-redux';
import _isEqual from 'lodash/isEqual';
import { DFPProvider } from 'reaxl-ads';
import { getPixallId } from 'atc-js';
import { getOptimizelyInstance, useOptimizelyFeature } from 'reaxl-optimizely';
import _get from 'lodash/get';
import ConfigContext from '@/contexts/ConfigContext';
import CacheKey from '@/utilities/CacheKey';
import { getMcmidCookie, getBehaviourialTarget } from '@/utilities';
import PersonalizationEngineModule from '@/modules/PersonalizationEngineModule';

const getExperimentVariants = (userInfo, activeExperiments) => {
    let experiments = activeExperiments;
    const optimizely = getOptimizelyInstance();
    if (optimizely) {
        const experimentsMap = _get(optimizely.getOptimizelyConfig(), 'experimentsMap', {});
        const experimentKeys = Object.keys(experimentsMap);
        experiments = experimentKeys.reduce((acc, exp) => {
            if (acc.length) {
                acc += '|';
            }
            return acc + _get(experimentsMap, exp + '.id') + ':'
                + _get(_get(experimentsMap, exp + '.variationsMap.' + optimizely.getVariation(exp, userInfo.id, userInfo.attributes)), 'id');
        }, experiments);
    }
    return experiments;
};

function areEqual(prevProps, nextProps, prev, next) {
    const {
        adConfig,
        adUnit,
        pageLevelTargeting,
        pixallId,
        forceUpdateComponent,
    } = prevProps;

    const adConfigEquality = adConfig === nextProps.adConfig;
    const adUnitEquality = adUnit === nextProps.adUnit;
    const targetingEquality = _isEqual(pageLevelTargeting, nextProps.pageLevelTargeting);
    const idEquality = pixallId === nextProps.pixallId;
    const isUpdatedEquality = forceUpdateComponent === nextProps.forceUpdateComponent;

    return adConfigEquality
        && adUnitEquality
        && targetingEquality
        && idEquality
        && isUpdatedEquality;
}

function AdsProviderContainer(props) {
    const {
        pageInst,
        adConfig,
        ...rest
    } = props;

    const { site } = useContext(ConfigContext);
    const { pageLevelTargeting = {}, prebidAdapters, prebidHeaderBidding = false, headerBidding = false, adUnit } = adConfig || {};

    const personalization = useSelector(PersonalizationEngineModule.duck.selectors.selectPersonalization);
    const pePreferences = useSelector(PersonalizationEngineModule.duck.selectors.selectPreferences);
    const activeExperiments = useSelector(PersonalizationEngineModule.duck.selectors.selectExperimentsFormatted);

    const userInfo = useOptimizelyFeature().getUserInfo();

    const pixallId = getPixallId() || 'nl';

    if (!site.adsEnabled) {
        return rest.children;
    }
    const targetingChangeWithoutRefreshAds = ['pixall_visitorid'];

    if (pageInst) {
        pageLevelTargeting.pginst = String(pageInst);
    }

    const providerProps = {
        ...rest,
        loadPrebid: prebidHeaderBidding,
        headerBidding,
        prebidPath: CacheKey.add('/resources/js/bundle/prebid.js', false),
        openWrapUrl: site.enableOpenWrap && site.openWrapUrl,
        prebidAdapters,
        targetingChangeWithoutRefreshAds,
        personalization,
        pePreferences,
        adUnit,
    };

    if (pageLevelTargeting) {
        providerProps.pageLevelTargeting = {
            ...pageLevelTargeting,
            mcmid: getMcmidCookie() || 'nl',
            pixall_visitorid: pixallId,
            btc: getBehaviourialTarget() || 'nl',
            tid: getExperimentVariants(userInfo, activeExperiments),
        };
    }

    providerProps.enableAdLibrariesLoading = true;

    return (
        <DFPProvider
            {...providerProps}
        />
    );
}

export default (memo(AdsProviderContainer, areEqual));
